import React, { useState } from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { Pad, Operadora, Equipo } from '../../types';
import { padFormFields } from '../../config/formFields';

interface PadsSectionProps {
  data: {
    pads: Pad[];
    operadoras: Operadora[];
    equipos: Equipo[];
  };
  operations: {
    handlePadSubmit: (data: Record<string, any>) => Promise<void>;
    handlePadDelete: (item: Pad) => Promise<void>;
    handlePadEdit: (item: Pad, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const PadsSection: React.FC<PadsSectionProps> = ({ data, operations, userPermissions }) => {
  // Filter state for custom view
  const [selectedOperadoraId, setSelectedOperadoraId] = useState<string>('');
  const [selectedEquipoId, setSelectedEquipoId] = useState<string>('');
  const [newPadName, setNewPadName] = useState<string>('');

  // Filter equipos based on operadora selection
  const filteredEquipos = selectedOperadoraId
    ? data.equipos.filter(eq => eq.habilitado && eq.operadoraId === selectedOperadoraId)
    : [];

  // Filter pads based on exact operadora+equipo combination
  const filteredPads = (selectedOperadoraId && selectedEquipoId)
    ? data.pads.filter(pad =>
        pad.operadoraId === selectedOperadoraId && pad.equipoId === selectedEquipoId)
    : [];

  // Handle new pad creation
  const handleAddPad = async () => {
    if (!newPadName.trim() || !selectedOperadoraId || !selectedEquipoId) {
      alert('⚠️ Debes seleccionar operadora, equipo y escribir un nombre para el pad');
      return;
    }

    try {
      await operations.handlePadSubmit({
        nombre: newPadName.trim(),
        operadoraId: selectedOperadoraId,
        equipoId: selectedEquipoId
      });
      setNewPadName('');
      alert('✅ Pad agregado exitosamente');
    } catch (error) {
      console.error('Error adding pad:', error);
      alert('❌ Error al agregar pad');
    }
  };

  // Table column configurations
  const padColumns: TableColumn<Pad>[] = [
    {
      key: 'nombre',
      title: 'Nombre del Pad',
      width: '30%',
      editable: true,
      editType: 'text',
      onEdit: operations.handlePadEdit
    },
    {
      key: 'operadoraId',
      title: 'Operadora',
      width: '30%',
      editable: true,
      editType: 'select',
      selectOptions: data.operadoras.map(op => ({ value: op.id, label: op.nombre })),
      onEdit: operations.handlePadEdit,
      render: (value) => {
        const operadora = data.operadoras.find(op => op.id === value);
        return operadora?.nombre || '(Sin asignar)';
      }
    },
    {
      key: 'equipoId',
      title: 'Equipo',
      width: '25%',
      editable: true,
      editType: 'select',
      selectOptions: data.equipos.map(eq => ({ value: eq.id, label: eq.nombre })),
      onEdit: operations.handlePadEdit,
      render: (value) => {
        const equipo = data.equipos.find(eq => eq.id === value);
        return equipo?.nombre || '(Sin asignar)';
      }
    }
  ];

  // Quick Setup Handler for standard pads
  const handlePadsQuickSetup = async () => {
    if (data.operadoras.length === 0 || data.equipos.length === 0) {
      alert('⚠️ Primero debes crear operadoras y equipos antes de cargar pads.');
      return;
    }

    if (window.confirm('¿Cargar pads estándar? Esta acción creará pads de ejemplo con asignaciones de equipos.')) {
      const padTemplates = [
        { nombre: 'Pad Norte A-1', suffix: 'A' },
        { nombre: 'Pad Sur B-2', suffix: 'B' },
        { nombre: 'Pad Este C-3', suffix: 'C' },
        { nombre: 'Pad Oeste D-4', suffix: 'D' },
        { nombre: 'Pad Central E-5', suffix: 'E' }
      ];

      try {
        for (let i = 0; i < Math.min(padTemplates.length, data.equipos.length); i++) {
          const equipo = data.equipos[i];
          const operadora = data.operadoras.find(op => op.id === equipo.operadoraId);

          if (operadora) {
            await operations.handlePadSubmit({
              nombre: `${padTemplates[i].nombre} - ${operadora.nombre.split(' ')[0]}`,
              operadoraId: operadora.id,
              equipoId: equipo.id
            });
          }
        }
        alert('✅ Pads estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando pads:', error);
        alert('❌ Error cargando pads. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>📍 Gestión de Pads</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra las locaciones de perforación (pads) y sus asignaciones de operadoras y equipos
        </p>
      </div>

      {/* Custom Filtered Pads Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#333', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📍 Pads de Perforación
        </h3>

        {/* Cascading Filter Controls */}
        <div style={{
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          {/* Step 1: Operadora Selector (Always visible) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>1. Seleccionar Operadora:</label>
            <select
              value={selectedOperadoraId}
              onChange={(e) => {
                setSelectedOperadoraId(e.target.value);
                setSelectedEquipoId(''); // Reset equipo when operadora changes
                setNewPadName(''); // Reset pad name
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '180px'
              }}
            >
              <option value="">Seleccionar operadora...</option>
              {data.operadoras.filter(op => op.habilitada).map(operadora => (
                <option key={operadora.id} value={operadora.id}>
                  {operadora.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Step 2: Equipo Selector (Only appears when operadora is selected) */}
          {selectedOperadoraId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>2. Seleccionar Equipo:</label>
              <select
                value={selectedEquipoId}
                onChange={(e) => {
                  setSelectedEquipoId(e.target.value);
                  setNewPadName(''); // Reset pad name when equipo changes
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '180px'
                }}
              >
                <option value="">Seleccionar equipo...</option>
                {filteredEquipos.map(equipo => (
                  <option key={equipo.id} value={equipo.id}>
                    {equipo.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Step 3: Add New Pad Field (Only appears when both operadora and equipo are selected) */}
          {selectedOperadoraId && selectedEquipoId && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>3. Agregar Nuevo Pad:</label>
                <input
                  type="text"
                  value={newPadName}
                  onChange={(e) => setNewPadName(e.target.value)}
                  placeholder="Nombre del pad"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddPad()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '200px'
                  }}
                />
              </div>
              <button
                onClick={handleAddPad}
                disabled={!newPadName.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: newPadName.trim() ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: newPadName.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  marginTop: '18px'
                }}
              >
                ➕ Agregar
              </button>
            </>
          )}
        </div>

        {/* Filtered Pads List (Only appears when both operadora and equipo are selected) */}
        {selectedOperadoraId && selectedEquipoId && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              paddingBottom: '10px',
              borderBottom: '1px solid #eee'
            }}>
              <h4 style={{ color: '#333', margin: 0 }}>
                Pads existentes para {data.operadoras.find(op => op.id === selectedOperadoraId)?.nombre} - {data.equipos.find(eq => eq.id === selectedEquipoId)?.nombre}
              </h4>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {filteredPads.length} pad{filteredPads.length !== 1 ? 's' : ''}
              </span>
            </div>

            {filteredPads.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '10px'
              }}>
                {filteredPads.map(pad => (
                  <div key={pad.id} style={{
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    transition: 'box-shadow 0.2s'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      color: '#333',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>📍 {pad.nombre}</span>
                      <button
                        onClick={() => operations.handlePadDelete(pad)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Eliminar pad"
                      >
                        🗑️
                      </button>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      🏢 {data.operadoras.find(op => op.id === pad.operadoraId)?.nombre}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      🔧 {data.equipos.find(eq => eq.id === pad.equipoId)?.nombre}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #ddd'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                  No hay pads para esta combinación
                </div>
                <div style={{ fontSize: '14px' }}>
                  Agrega el primer pad usando el campo de arriba
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Setup Button */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            onClick={handlePadsQuickSetup}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📍 Cargar Pads Estándar
          </button>
        </div>
      </div>

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#fff3e0',
        borderRadius: '12px',
        border: '2px solid #FF9800'
      }}>
        <h3 style={{ color: '#E65100', marginBottom: '15px' }}>📊 Resumen de Pads</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              📍 Total Pads
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {data.pads.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🔧 Con Equipos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.pads.filter(pad => pad.equipoId).length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🏢 Operadoras
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2196F3'
            }}>
              {new Set(data.pads.map(pad => pad.operadoraId)).size}
            </div>
          </div>
        </div>

        {/* Pads por Operadora */}
        {data.operadoras.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#E65100', marginBottom: '10px' }}>Pads por Operadora:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
              {data.operadoras.map(operadora => {
                const operadoraPads = data.pads.filter(pad => pad.operadoraId === operadora.id);
                return (
                  <div key={operadora.id} style={{
                    padding: '15px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #ddd'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#E65100' }}>
                      {operadora.nombre}
                    </div>
                    {operadoraPads.length > 0 ? (
                      <div style={{ fontSize: '14px' }}>
                        {operadoraPads.map(pad => (
                          <div key={pad.id} style={{
                            marginBottom: '4px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span>📍 {pad.nombre}</span>
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {data.equipos.find(eq => eq.id === pad.equipoId)?.nombre || 'Sin equipo'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                        Sin pads asignados
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data.pads.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay pads registrados. Primero crea operadoras y equipos, luego usa la carga rápida.
          </div>
        )}
      </div>
    </>
  );
};